import { ErrorHandler, Injectable, inject } from '@angular/core';
import { NotificationService } from '@services/notification.service';
import { AuthService } from '@services/auth.service';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { TelegramService } from '@services/telegram.service';
import { StoreErrorHandlerService } from './store-error-handler.service';
import { WebSocketController } from '@services/web-socket.controller';
import { environment } from '@environments/environment';
import { AppInfoService } from '@services/app-info.service';
import { PlatformService } from '@services/platform.service';
import { SessionStorageService } from '@services/session-storage.service';

interface ErrorMassage {
  environment: string,
  timestamp: string,
  userIP?: string,
  appVersion: string,
  userID: number | string,
  device: string,
  deviceID: number | string,
  socketId: string,
  socketConnected: boolean | string,
  message: string,
  errorStack?: string,
}

@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler implements ErrorHandler {
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly telegramService = inject(TelegramService);
  private readonly storeErrorHandlerService = inject(StoreErrorHandlerService);
  private readonly webSocketController = inject(WebSocketController);
  private readonly appInfoService = inject(AppInfoService);
  private readonly platformService = inject(PlatformService);
  private readonly sessionStorageService = inject(SessionStorageService);

  private static readonly chunkLoadReloadTimestampStorageKey: string = 'pyjamChunkLoadReloadTimestamp';
  private static readonly chunkLoadReloadCooldownMs: number = 60_000;

  private readonly production: boolean = false;
  private isReloadingPageAfterChunkLoadError: boolean = false;

  public get isReloadingAfterChunkLoadError(): boolean {
    return this.isReloadingPageAfterChunkLoadError;
  }

  constructor() {
    this.production = environment.production;
  }

  public async throwError(error: any): Promise<void> {
    await this.handleError(error);
  }

  public async handleError(error: any): Promise<void> {
    this.storeErrorHandlerService.addError(error);

    if (this.reloadPageOnChunkLoadError(error)) {
      return;
    }

    if (environment.useSentry) {
      const payload: ErrorMassage = await this.createMessage(error);
      await this.telegramService.sendMessage(payload);
    } else {
      console.error(error);
    }
  }

  private async createMessage(error: any): Promise<ErrorMassage> {
    const environment: string = this.production ? 'Production' : 'Development';
    const timestamp: string = new Date().toISOString();
    // const publicIP: any = await firstValueFrom(this.httpClient.get('https://api.ipify.org?format=json'));
    // const userIP: string = publicIP.ip;
    const appVersion: string = 'WEB';
    const userID: number | string = this.authService.parsedToken?.userId || 'User does not authorized';
    const device: string = await this.phoneInfoService.getDeviceModelWithOS() || 'Device model is unavailable';
    const deviceID: number | string = this.notificationService.getUserDeviceId() || 'Has not been retrieved from the backend';
    const socketId: string = this.webSocketController.socketId || 'Socket ID is unavailable';
    const socketConnected: boolean | string = this.webSocketController.socketConnected || 'Socket connection status is unavailable';
    const fallbackMessage: string = typeof error?.toString === 'function' ? error.toString() : '';
    const message: string = typeof error?.message === 'string' && error.message.length > 0 ? error.message : fallbackMessage || 'The error message is unavailable';
    const errorStack: string = error.stack || 'The call stack is unavailable';

    const newMessage: ErrorMassage = {
      environment,
      timestamp,
      // userIP,
      appVersion,
      userID,
      device,
      deviceID,
      socketId,
      socketConnected,
      message,
    };

    if (message.length < 70) {
      newMessage.errorStack = errorStack;
    }

    newMessage.appVersion = await this.appInfoService.getFullAppVersion();

    return newMessage;
  }

  private reloadPageOnChunkLoadError(error: any): boolean {
    if (!this.isChunkLoadError(error)) {
      return false;
    }

    if (!this.platformService.isBrowser || this.platformService.isDevice || typeof window === 'undefined') {
      return false;
    }

    if (this.wasReloadedAfterRecentChunkLoadError()) {
      return false;
    }

    this.sessionStorageService.setItem(
      CustomErrorHandler.chunkLoadReloadTimestampStorageKey,
      Date.now().toString()
    );
    this.isReloadingPageAfterChunkLoadError = true;

    console.error('Chunk loading error, reloading the app.', error);
    window.location.reload();
    return true;
  }

  private isChunkLoadError(error: any): boolean {
    return error?.name === 'ChunkLoadError'
      || (typeof error?.message === 'string' && error.message.includes('ChunkLoadError'));
  }

  private wasReloadedAfterRecentChunkLoadError(): boolean {
    const storedReloadTimestamp: any | null = this.sessionStorageService.getItem(
      CustomErrorHandler.chunkLoadReloadTimestampStorageKey
    );

    if (typeof storedReloadTimestamp !== 'string') {
      return false;
    }

    const reloadTimestamp: number = Number(storedReloadTimestamp);
    return Number.isFinite(reloadTimestamp)
      && Date.now() - reloadTimestamp < CustomErrorHandler.chunkLoadReloadCooldownMs;
  }
}
